import { createTheme, Theme, ThemeOptions, Shadows, alpha } from '@mui/material/styles';
import type { ThemeName } from 'atoms/themeAtom';
import type { Powertrain } from 'atoms/powertrainAtom';

// gray=既定テーマ。無彩色グレーの chrome（ツールバー／背景）に、操作系を高視認化するブルーの
// アクセント（primary）を合わせる。ツールバー自体は buildComponents の MuiAppBar 上書きでグレーに固定する。
// white/black/red はテーマ名の字義に縛られず、ビジュアル優先で配色を決める。
//   white=Slack 風オーベルジーヌ（紫）/ black=ダーク chrome＋シアン操作系 / red=ワイン〜ローズの赤系。
// gray は無彩色（色相ゼロ）を厳守する。
const PALETTES: Record<ThemeName, ThemeOptions['palette']> = {
    gray: {
        mode: 'light',
        // primary=ブルー: ボタン／セレクタ／フォーカス／選択状態など操作系の視認性を担保する。
        primary: { main: '#1565C0', light: '#1E88E5', dark: '#0D47A1', contrastText: '#FFFFFF' },
        // secondary=ディープオレンジ: primary(青) と明確に異なる第2アクセント（登録元 Chip・ユーザーアイコン）。
        secondary: { main: '#E65100', light: '#FB8C00', dark: '#BF360C', contrastText: '#FFFFFF' },
        background: { default: '#F5F5F5', paper: '#FAFAFA' },
    },
    white: {
        mode: 'light',
        primary: { main: '#4A154B', light: '#6B2E6C', dark: '#3A0B3B', contrastText: '#FFFFFF' },
        secondary: { main: '#007A5A', light: '#36A084', dark: '#005C43', contrastText: '#FFFFFF' },
        background: { default: '#F7F7F8', paper: '#FFFFFF' },
    },
    black: {
        mode: 'dark',
        // 操作系を暗背景で映えさせるため primary をシアンにし、文字は暗色でコントラストを確保する。
        // chrome（AppBar/背景/サイドバー）はダークのまま＝スタイリッシュさを維持する。
        primary: { main: '#1FA7DE', light: '#4FBEE8', dark: '#1582AE', contrastText: '#06141B' },
        // secondary=マゼンタ: シアンと対をなすネオンアクセント（ユーザーアイコン・登録元 Chip）。
        secondary: { main: '#E0529B', light: '#EA7BB4', dark: '#B23B79', contrastText: '#FFFFFF' },
        background: { default: '#0E1116', paper: '#181C22' },
    },
    red: {
        mode: 'dark',
        primary: { main: '#7A1623', light: '#A0212F', dark: '#560E18', contrastText: '#FFFFFF' },
        secondary: { main: '#E01E5A', light: '#EB527E', dark: '#A81444', contrastText: '#FFFFFF' },
        background: { default: '#1B0E10', paper: '#241317' },
    },
};

// gray テーマのツールバー基準色（グラデ透過部の下地フォールバック）。無彩色を厳守する。
const GRAY_TOOLBAR_BG = '#616161';

// BEV/HEV の識別色。テーマに依存せず常に固定で、BEV と HEV を別色にする。
// PowertrainToggle の切替トグルと CalcSetIndicator の履歴 Chip で共有する。
export const POWERTRAIN_COLORS: Record<Powertrain, { main: string; hover: string; contrast: string }> = {
    bev: { main: '#00796B', hover: '#00695C', contrast: '#FFFFFF' }, // ティール
    hev: { main: '#6A1B9A', hover: '#4A148C', contrast: '#FFFFFF' }, // パープル
};

// テーマ毎の装飾（グラデーション／透過）。色は AppBar・背景・サイドバー・primary ボタンに効かせる。
// gray は無彩色（色相ゼロ）を厳守し、グラデも白〜グレーのみ・primaryButton も持たせない。
// AppBar の白文字コントラスト(AA)を保てる明度域にとどめる。
type ThemeDecor = {
    appBar: string; // AppBar 背景グラデ（半透明＋blur 前提）
    appBackground: string; // body 背景グラデ（subtle）
    sidebar: string; // Drawer 背景グラデ（不透明・本文と差をつける）
    primaryButton?: string; // contained primary のグラデ（gray は無し＝無彩色維持）
};
const DECOR: Record<ThemeName, ThemeDecor> = {
    gray: {
        // 無彩色のダークグレー斜めグラデ。白文字 AA（最も明るい #686868 でも 4.4:1）を確保。
        appBar: 'linear-gradient(135deg, rgba(90,90,90,0.95) 0%, rgba(104,104,104,0.92) 100%)',
        appBackground: 'linear-gradient(180deg, #F7F7F8 0%, #ECECEE 100%)',
        sidebar: 'linear-gradient(180deg, #FAFAFA 0%, #F1F1F2 100%)',
    },
    white: {
        // Slack オーベルジーヌ系の紫グラデ。
        appBar: 'linear-gradient(135deg, rgba(58,11,59,0.95) 0%, rgba(97,31,105,0.92) 100%)',
        appBackground: 'linear-gradient(180deg, #FBF8FC 0%, #F1EAF3 100%)',
        sidebar: 'linear-gradient(180deg, #FFFFFF 0%, #F5EFF6 100%)',
        primaryButton: 'linear-gradient(135deg, #4A154B 0%, #6B2E6C 100%)',
    },
    black: {
        // chrome はダークグラデで統一（スタイリッシュ）。ボタンは明るいシアングラデで暗背景に映えさせる。
        appBar: 'linear-gradient(135deg, rgba(13,16,20,0.96) 0%, rgba(26,31,38,0.92) 100%)',
        appBackground: 'linear-gradient(180deg, #0E1116 0%, #161A20 100%)',
        sidebar: 'linear-gradient(180deg, #1C212A 0%, #15191F 100%)',
        primaryButton: 'linear-gradient(135deg, #1FA7DE 0%, #36BEEF 100%)',
    },
    red: {
        // ワインレッド→ビビッドなマゼンタ寄りの赤グラデ。
        appBar: 'linear-gradient(135deg, rgba(86,14,24,0.96) 0%, rgba(138,26,43,0.92) 100%)',
        appBackground: 'linear-gradient(180deg, #150A0C 0%, #1F0E11 100%)',
        sidebar: 'linear-gradient(180deg, #2A161A 0%, #1B0E10 100%)',
        primaryButton: 'linear-gradient(120deg, #7A1623 0%, #A0212F 70%, #C42A4E 135%)',
    },
};

// モダン化(色は不変・質感のみ): MUI 既定の濃い elevation 影ランプを、低 alpha の二層ソフト影に置換する。
// 影色は青みがかったスレート(15,23,42)で純黒より柔らかく今日的に見せる。e=elevation(0..24)。
const softShadow = (e: number): string => {
    if (e <= 0) return 'none';
    const a1 = 0.04 + e * 0.004; // 近接層: 輪郭付け
    const a2 = 0.05 + e * 0.006; // 拡散層: 浮遊感
    const y1 = Math.max(1, Math.round(e * 0.4));
    const b1 = Math.max(2, Math.round(e * 1.2));
    const y2 = Math.max(2, Math.round(e * 0.9));
    const b2 = Math.max(4, Math.round(e * 2.6));
    return `0px ${y1}px ${b1}px rgba(15,23,42,${a1.toFixed(3)}), 0px ${y2}px ${b2}px rgba(15,23,42,${a2.toFixed(3)})`;
};
const softShadows = Array.from({ length: 25 }, (_, i) => softShadow(i)) as unknown as Shadows;

const baseOptions: ThemeOptions = {
    shape: { borderRadius: 10 }, // 既定 4px は角が立って古く見えるため一段大きく
    shadows: softShadows,
    // トランジションの duration/easing を明示し、component override から SSOT として参照する。
    // 体感品質の底上げが目的で、reduced-motion 時は MuiCssBaseline の global で実質無効化する。
    transitions: {
        easing: {
            easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
            easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
            easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
            sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
        },
        duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            standard: 300,
            complex: 375,
            enteringScreen: 225,
            leavingScreen: 195,
        },
    },
    typography: {
        fontFamily: ['"Roboto"', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
        // 見出し/操作系の weight 階層を明確化（既定は単調で平板に見える）。
        h4: { fontWeight: 700, letterSpacing: '-0.01em' },
        h5: { fontWeight: 700, letterSpacing: '-0.01em' },
        h6: { fontWeight: 700, letterSpacing: '-0.005em' },
        subtitle1: { fontWeight: 600 },
        subtitle2: { fontWeight: 600 },
        button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 }, // 大文字変換を止めるのがモダン化の要
        overline: { fontWeight: 600, letterSpacing: '0.08em' },
    },
};

// テーマ非依存の共通 component overrides（色/グラデが絡むものは buildComponents 側で付与）。
const baseComponents: NonNullable<ThemeOptions['components']> = {
    MuiPaper: {
        // dark mode の elevation オーバーレイ（白み）を切り、フラットで澄んだ面にする。
        styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
        defaultProps: { elevation: 0 },
        // ハイブリッド: 淡いソフト影 + hairline 境界 + 角丸14 でカードを今日的に。
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: 14,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: softShadow(3),
                // hover でわずかに浮かせて「触れられる」印象に。transform はリフローを起こさない。
                transition: theme.transitions.create(['box-shadow', 'transform'], {
                    duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                    boxShadow: softShadow(6),
                    transform: 'translateY(-2px)',
                },
            }),
        },
    },
    MuiToggleButton: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
    },
    MuiOutlinedInput: {
        styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiMenu: {
        // メニュー/ドロップダウンを角丸・ソフト影・余白付きに（既定は角張って影が濃い）。
        styleOverrides: {
            paper: { borderRadius: 12, marginTop: 4, boxShadow: softShadow(6) },
            list: { paddingTop: 6, paddingBottom: 6 },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: 8,
                marginLeft: 6,
                marginRight: 6,
                transition: theme.transitions.create(['background-color', 'color'], {
                    duration: theme.transitions.duration.shortest,
                }),
            }),
        },
    },
    MuiListItemButton: {
        // SideNav の選択差し色（powertrain 別アクセント）切替を含め、選択/hover を滑らかに補間する。
        styleOverrides: {
            root: ({ theme }) => ({
                borderRadius: 8,
                transition: theme.transitions.create(['background-color', 'color'], {
                    duration: theme.transitions.duration.shorter,
                }),
            }),
        },
    },
    MuiTableCell: {
        // 区切り線を divider に統一して薄く、ヘッダは淡いトーン地＋太字でヒエラルキーを付ける。
        styleOverrides: {
            root: ({ theme }) => ({ borderColor: theme.palette.divider }),
            head: ({ theme }) => ({
                fontWeight: 700,
                color: theme.palette.text.secondary,
                backgroundColor:
                    theme.palette.mode === 'light'
                        ? alpha(theme.palette.text.primary, 0.03)
                        : alpha(theme.palette.common.white, 0.04),
            }),
        },
    },
    MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16 } },
    },
    MuiAlert: {
        styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: { borderRadius: 8, fontSize: '0.75rem', paddingLeft: 10, paddingRight: 10, paddingTop: 6, paddingBottom: 6 },
        },
    },
    MuiSkeleton: {
        // 体感速度向上: loading 中のプレースホルダ。wave アニメ＋角丸で実体の表/グラフを想起させる。
        defaultProps: { animation: 'wave' },
        styleOverrides: { root: { borderRadius: 8 } },
    },
};

const buildComponents = (name: ThemeName): ThemeOptions['components'] => {
    const decor = DECOR[name];
    return {
        ...baseComponents,
        MuiCssBaseline: {
            // body にテーマ背景グラデを敷く。スクロールバーは細く・角丸に（mode で thumb 色を反転）。
            styleOverrides: (theme) => ({
                body: { background: decor.appBackground, backgroundAttachment: 'fixed' },
                '*::-webkit-scrollbar': { width: 10, height: 10 },
                '*::-webkit-scrollbar-thumb': {
                    borderRadius: 8,
                    backgroundColor: alpha(theme.palette.text.primary, 0.18),
                },
                '*::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.28),
                },
                // a11y: キーボード操作時のみフォーカスリングを可視化し、マウスクリックでは残さない。
                ':focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                },
                ':focus:not(:focus-visible)': { outline: 'none' },
                // reduced-motion: 全 transition/animation を実質無効化（即時表示）。
                // MUI Transition のインライン style も上書きするため !important を付す。
                '@media (prefers-reduced-motion: reduce)': {
                    '*, *::before, *::after': {
                        animationDuration: '0.01ms !important',
                        animationIterationCount: '1 !important',
                        transitionDuration: '0.01ms !important',
                        scrollBehavior: 'auto !important',
                    },
                },
            }),
        },
        // AppBar: テーマ別グラデ＋frosted blur＋ソフト影でフラットかつリッチに。白文字を維持。
        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { backdropFilter: 'blur(8px)', boxShadow: softShadow(2) },
                colorPrimary: ({ theme }) => ({
                    // 透過グラデの下地フォールバック（blur で背後が薄く透ける）。
                    backgroundColor:
                        name === 'gray'
                            ? alpha(GRAY_TOOLBAR_BG, 0.92)
                            : alpha(theme.palette.primary.main, 0.92),
                    backgroundImage: decor.appBar,
                    color: '#FFFFFF',
                }),
            },
        },
        // SideNav(Drawer): 本文背景と差をつける subtle グラデ＋右 hairline。可読性のため不透明。
        MuiDrawer: {
            styleOverrides: {
                paper: ({ theme }) => ({
                    backgroundImage: decor.sidebar,
                    borderRight: `1px solid ${theme.palette.divider}`,
                }),
            },
        },
        // ボタン: contained のベタ影を排し平滑に。color テーマは primary をグラデ化（gray はソリッド維持）。
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                    // press 時のわずかな縮小で触覚的フィードバックを与える。色/影/グラデ明度も滑らかに補間。
                    transition: theme.transitions.create(
                        ['background-color', 'box-shadow', 'border-color', 'color', 'transform', 'filter'],
                        { duration: theme.transitions.duration.shorter },
                    ),
                    '&:active': { transform: 'scale(0.98)' },
                }),
                ...(decor.primaryButton
                    ? {
                          containedPrimary: {
                              backgroundImage: decor.primaryButton,
                              '&:hover': { backgroundImage: decor.primaryButton, filter: 'brightness(1.08)' },
                              // disabled でグラデが残ると見栄えが悪いため明示的に解除。
                              '&.Mui-disabled': { backgroundImage: 'none' },
                          },
                      }
                    : {}),
            },
        },
    };
};

// プリセット名から MUI Theme を生成するファクトリ。themeAtom と連動して使う。
export const buildTheme = (name: ThemeName): Theme =>
    createTheme({
        ...baseOptions,
        palette: PALETTES[name],
        components: buildComponents(name),
    });

// 既定テーマ（gray）。動的切替を行わない箇所からの後方互換 default import 用。
const theme = buildTheme('gray');
export default theme;
